import { useState, useEffect } from "react";
import GitInfo from "react-git-info/macro";

import BaseWithHeader from "./templates/BaseWithHeader/BaseWithHeader";
import {
  useGetConfigQuery,
  useGetFeatureListQuery,
  useGetSearchTagsListQuery,
  useHeartbeatPingMutation,
} from "./redux/services/config";
import { errorHandler } from "./utils";
import packageJSON from "../package.json";
import "./App.css";
import { getScreenId } from "./utils/getScreenId";
import { logDebugData } from "utils/logDebugData";
import { useQueryParams } from "hooks/useQueryParams";
import { useTranslation } from "react-i18next";
import i18n from "i18n";

export enum LAYOUTS {
  PORTRAIT = "portrait",
  LANDSCAPE = "landscape",
}

function App() {
  const screenId = getScreenId();

  const [uptime, setUptime] = useState<string | null>(null);
  const { t } = useTranslation();

  const { isDebugMode } = useQueryParams();

  const {
    data,
    isError: isConfigError,
    isLoading: isConfigLoading,
    error: configError,
  } = useGetConfigQuery(screenId);

  const {
    data: featuresResponse,
    isError: isFeaturesError,
    isLoading: isFeaturesLoading,
    error: featuresError,
  } = useGetFeatureListQuery(screenId);

  const {
    data: searchTagsResponse,
    isError: isSearchTagsError,
    isLoading: isSearchTagsLoading,
    error: searchTagsError,
  } = useGetSearchTagsListQuery(screenId);

  const [heartbeatPing] = useHeartbeatPingMutation();

  const { commit, branch } = GitInfo();

  useEffect(() => {
    if (!data?.languages) return;

    data.languages.available.map(({ id }) => i18n.loadLanguages(id));
    i18n.changeLanguage(data.languages.default.id);
    document.documentElement.setAttribute(
      "dir",
      data.languages.default.orientation,
    );
  }, [data?.languages]);

  useEffect(() => {
    if (data) {
      console.log(
        `Version: ${packageJSON.version}\nBranch: ${branch}\nCommit hash: ${commit.hash}`,
      );
      console.log(data);

      isDebugMode && logDebugData(data);

      const currentTime = new Date(Date.now()).toISOString();
      setUptime(currentTime);

      !isDebugMode &&
        heartbeatPing({
          id: screenId,
          up_since: currentTime,
          screen: { width: window.innerWidth, height: window.innerHeight },
          frontend: { version: packageJSON.version, commit: commit.hash },
        });
    }
  }, [data, heartbeatPing, branch, commit.hash, screenId, isDebugMode]);

  useEffect(() => {
    if (uptime && !isDebugMode) {
      setInterval(() => {
        heartbeatPing({
          id: screenId,
          up_since: uptime,
          screen: { width: window.innerWidth, height: window.innerHeight },
          frontend: { version: packageJSON.version, commit: commit.hash },
        });
      }, 1000 * 60);
    }
  }, [heartbeatPing, screenId, uptime, commit.hash, isDebugMode]);

  if (isConfigError) {
    errorHandler(configError);
    return null;
  }

  if (isFeaturesError) {
    errorHandler(featuresError);
    return null;
  }

  if (isSearchTagsError) {
    console.error(searchTagsError);
  }

  if (isConfigLoading || isFeaturesLoading || isSearchTagsLoading)
    return <div>{t("app.loading")}</div>;
  if (!data) return <div>{t("app.no_config")}</div>;
  if (!featuresResponse) return <div>{t("app.no_feature_data")}</div>;

  return (
    <BaseWithHeader
      data={data}
      preloadedFeatures={featuresResponse?.data}
      searchTags={searchTagsResponse?.data}
      uptime={uptime}
    />
  );
}

export default App;
